import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '../components/ui';
import ReportTable from '../reports/ReportTable';
import { useReportData } from '../reports/useReportData';
import type { ReportListItem } from '../reports/types';
import { useTranslation } from 'react-i18next';

interface ReportsProps {
  apiUrl: string;
}

const Reports: React.FC<ReportsProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [listError, setListError] = useState('');
  const [listLoading, setListLoading] = useState(true);

  const { meta, data, loading, error, sort, dir, search, setSort, setPage, setSearch } = useReportData(apiUrl, selected);
  const [execDays, setExecDays] = useState('30');
  const [busy, setBusy] = useState<'' | 'csv' | 'pdf' | 'exec'>('');
  const [exportError, setExportError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${apiUrl}/admin/reports`);
        const body = await r.json();
        if (!r.ok) throw new Error(body?.error || 'Failed to load reports');
        const list: ReportListItem[] = body.reports || [];
        setReports(list);
        if (list.length && !selected) setSelected(list[0].id);
      } catch (e: any) {
        setListError(e.message || 'Failed to load reports');
      } finally {
        setListLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  // Group the report list by category for the sidebar.
  const grouped = useMemo(() => {
    const map = new Map<string, ReportListItem[]>();
    for (const r of reports) {
      const cat = r.category || 'Reports';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(r);
    }
    return [...map.entries()];
  }, [reports]);

  // Downloads go through fetch (auth header is injected globally) → blob.
  const download = async (path: string, fallbackName: string, kind: 'csv' | 'pdf' | 'exec') => {
    setBusy(kind);
    setExportError('');
    try {
      const r = await fetch(`${apiUrl}${path}`);
      if (!r.ok) {
        const body = await r.json().catch(() => null);
        throw new Error(body?.error || `Export failed (${r.status})`);
      }
      const disposition = r.headers.get('Content-Disposition') || '';
      const name = /filename="([^"]+)"/.exec(disposition)?.[1] || fallbackName;
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setExportError(e.message || 'Export failed');
    } finally {
      setBusy('');
    }
  };

  const handleExport = (format: 'csv' | 'pdf') => {
    if (!selected) return;
    const params = new URLSearchParams({ format });
    if (sort) params.set('sort', sort);
    if (dir) params.set('dir', dir);
    if (search.trim()) params.set('q', search.trim());
    download(`/admin/reports/${encodeURIComponent(selected)}/export?${params}`, `${selected}.${format}`, format);
  };

  const handleExecutive = () =>
    download(`/admin/reports/_executive/pdf?days=${execDays}`, `agenti5-executive-report.pdf`, 'exec');

  return (
    <div className="page-content reports-page">
      <h1><Icon name="clipboardList" size={22} /> {t('pages.reports.reports')}</h1>
      <p>{t('pages.reports.metadataDrivenReportsAcrossYourAgenti5')}</p>

      <div className="report-card exec-report-card">
        <div className="report-card-head">
          <div className="report-card-title">
            <h2><Icon name="shield" size={18} /> {t('pages.reports.executiveReport')}</h2>
            <p>{t('pages.reports.executiveReportDesc')}</p>
          </div>
          <div className="report-actions">
            <select
              className="ent-input"
              value={execDays}
              onChange={(e) => setExecDays(e.target.value)}
              aria-label={t('pages.reports.reportingPeriod')}
            >
              <option value="7">{t('pages.reports.lastNDays', { n: 7 })}</option>
              <option value="30">{t('pages.reports.lastNDays', { n: 30 })}</option>
              <option value="90">{t('pages.reports.lastNDays', { n: 90 })}</option>
            </select>
            <button className="ent-btn sm primary" onClick={handleExecutive} disabled={busy === 'exec'}>
              <Icon name="download" size={14} /> {busy === 'exec' ? t('pages.reports.generating') : t('pages.reports.downloadPDF')}
            </button>
          </div>
        </div>
      </div>
      {exportError && <div className="report-warning">{exportError}</div>}

      <div className="reports-layout">
        <aside className="reports-nav">
          {listLoading && <div className="report-loading-block">{t('pages.reports.loadingReports')}</div>}
          {!listLoading && listError && <div className="report-warning">{listError}</div>}
          {grouped.map(([cat, items]) => (
            <div key={cat} className="reports-nav-group">
              <div className="reports-nav-cat">{cat}</div>
              {items.map((r) => (
                <button
                  key={r.id}
                  className={`reports-nav-item${selected === r.id ? ' active' : ''}`}
                  onClick={() => setSelected(r.id)}
                  title={r.description}
                >
                  {r.title}
                </button>
              ))}
            </div>
          ))}
          {!listLoading && !listError && reports.length === 0 && (
            <div className="report-empty">{t('pages.reports.noReportsAvailable')}</div>
          )}
        </aside>

        <section className="reports-main">
          {error && <div className="report-warning">{error}</div>}
          {meta ? (
            <div className="report-card">
              <div className="report-card-head">
                <div className="report-card-title">
                  <h2>{meta.title}</h2>
                  {meta.description && <p>{meta.description}</p>}
                </div>
                <div className="report-actions">
                  {meta.export?.csv !== false && (
                    <button className="ent-btn sm" onClick={() => handleExport('csv')} disabled={busy === 'csv'}>
                      <Icon name="download" size={14} /> {t('pages.reports.exportCSV')}
                    </button>
                  )}
                  {meta.export?.pdf !== false && (
                    <button className="ent-btn sm" onClick={() => handleExport('pdf')} disabled={busy === 'pdf'}>
                      <Icon name="download" size={14} /> {busy === 'pdf' ? t('pages.reports.generating') : t('pages.reports.exportPDF')}
                    </button>
                  )}
                </div>
              </div>

              <ReportTable
                meta={meta}
                data={data}
                loading={loading}
                sort={sort}
                dir={dir}
                onSort={setSort}
                onPage={setPage}
                onSearch={setSearch}
              />
            </div>
          ) : (
            !error && (
              <div className="report-empty">{listLoading ? t('pages.reports.loadingReports') : t('pages.reports.selectAReport')}</div>
            )
          )}
        </section>
      </div>
    </div>
  );
};

export default Reports;
